import React, { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Box from './Box';
import { TButtonVariants, TSpacing } from './types';

import { handleButtonVariants } from '../../../styles/Shared/ButtonStyles';
import { customMarginStyles, customPaddingStyles } from '../../../styles/Shared/SharedStyles';

type TButtonProps = {
    size?: 'xs' | 's' | 'm' | 'l';
    disabled?: boolean;
    animateIconLeft?: boolean;
};

const buttonStyles = css`
    width: ${({ width }) => (width ? width : null)};
    height: ${({ height }) => (height ? height : null)};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    ${customMarginStyles};
    padding: ${({ size }) => {
        if (size === 'xs') return '10px 16px';
        if (size === 's') return '13px 20px';
        if (size === 'm') return '16px 24px';
        if (size === 'l') return '19px 32px';
        return 0;
    }};
    ${customPaddingStyles};
    font-family: inherit;
    text-decoration: none;
    background: none;
    cursor: pointer;
    z-index: 1;
    ${(props: TButtonVariants & TButtonProps & CSSProperties) => handleButtonVariants(props)};

    &:hover {
        transition: all 0.3s ease-in-out;
        transform: scale(1.05);
    }
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

export const StyledButton = styled.button<CSSProperties & TButtonProps & TButtonVariants & TSpacing>`
    ${buttonStyles}
`;

export const LinkButton = styled(Link)<CSSProperties & TButtonProps & TButtonVariants & TSpacing>`
    ${buttonStyles}
`;

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    children: React.ReactNode;
    size?: 'xs' | 's' | 'm' | 'l';
    disabled?: boolean;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    animateIconLeft?: boolean;
}

export const Button = (props: Props & TButtonVariants & CSSProperties): JSX.Element => {
    const { children, variant, size, onClick, startIcon, endIcon, ...rest } = props;

    if (startIcon) {
        return (
            <StyledButton variant={variant} size={size} onClick={onClick} {...rest}>
                <Box as="span" display="flex" alignSelf="center" pr="sm">
                    {startIcon}
                </Box>
                {children}
            </StyledButton>
        );
    }

    if (endIcon) {
        return (
            <StyledButton variant={variant} size={size} onClick={onClick} {...rest}>
                {children}
                <Box as="span" display="flex" alignSelf="center" pl="sm">
                    {endIcon}
                </Box>
            </StyledButton>
        );
    }

    return (
        <StyledButton variant={variant} size={size} onClick={onClick} {...rest}>
            {children}
        </StyledButton>
    );
};
